import { MP_REPLAY_ID_HEADER } from '../constants';
import { getReplayId } from '../utils/getReplayId/getReplayId';
import { isSameOrigin } from '../utils/isSameOrigin/isSameOrigin';

/**
 * Tracks functions we have already wrapped, so patching is idempotent without tagging them.
 */
const patchedFns = new WeakSet<object>();

const isRequest = (input: RequestInfo | URL): input is Request =>
    (typeof Request !== 'undefined' && input instanceof Request) ||
    (typeof input === 'object' && input !== null && 'url' in input && 'headers' in input);

/**
 * Monkey-patches window.fetch to add the X-MP-REPLAY-Id header on same-origin requests when a
 * replay id is present in sessionStorage. Additive only, idempotent, and fail-safe
 */
export const patchFetch = (): void => {
    const original = window.fetch;
    if (!original || patchedFns.has(original)) {
        return;
    }

    const patched: typeof window.fetch = (input, init) => {
        try {
            const id = getReplayId();
            const url = isRequest(input) ? input.url : String(input);

            if (id && isSameOrigin(url)) {
                const originalHeaders = init?.headers ?? (isRequest(input) ? input.headers : undefined);
                const headers = new Headers(originalHeaders);

                if (!headers.has(MP_REPLAY_ID_HEADER)) {
                    headers.set(MP_REPLAY_ID_HEADER, id);

                    if (isRequest(input)) {
                        return original(new Request(input, { ...init, headers }));
                    }

                    return original(input, { ...init, headers });
                }
            }
        } catch {
            /* fall through to the original fetch */
        }

        return original(input, init);
    };

    patchedFns.add(patched);
    window.fetch = patched;
};
